import { useState } from "react";
import { useAsyncFn } from "react-use";
import { StringHelper } from "@/helpers";
import {
  UserMemberOptionData,
  UserTenantOptionData,
  UserService,
  UserCategory,
} from "@/domains/user";
import {
  UserOptionMemberChargeFrom,
  UserOptionMemberChargeTime,
} from "@/domains/user/option/enum";
import {
  UserUserFormData,
  UserBaseOptionFormData,
  UserMemberOptionFormData,
  UserTenantOptionFormData,
  UserApiAccessFormData,
  UserWebAccessFormData,
} from "../types";
// components
import { Button, Modal, Steps } from "antd";
import { PlusCircleTwoTone } from "@ant-design/icons";
import { Step0Form } from "./Step0Form";
import { Step1Form } from "./Step1Form";
import { Step2Form } from "./Step2Form";
import { Step3Form } from "./Step3Form";
import { Step4Form } from "./Step4Form";
import { TransferCalcRound } from "@/domains/transfer";

export const CreateModal: React.FC<{
  onCreate: () => void;
  onFail: () => void;
}> = ({ onCreate, onFail }) => {
  const [isOpen, setIsOpen] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);

  const [userFormData, setUserFormData] = useState<UserUserFormData>();

  const [baseOptionFormData, setBaseOptionFormData] =
    useState<UserBaseOptionFormData>();

  const [tenantOptionFormData, setTenantOptionFormData] =
    useState<UserTenantOptionFormData>();

  const [memberOptionFormData, setMemberOptionFormData] =
    useState<UserMemberOptionFormData>();

  const [apiAccessOptionFormData, setApiAccessOptionFormData] =
    useState<UserApiAccessFormData>();

  const [webAccessOptionFormData, setWebAccessOptionFormData] =
    useState<UserWebAccessFormData>();

  const [state, createUser] = useAsyncFn(
    async (formData: {
      user: UserUserFormData;
      base: UserBaseOptionFormData;
      tenant: UserTenantOptionFormData;
      member: UserMemberOptionFormData;
      apiAccess: UserApiAccessFormData;
      webAccess: UserWebAccessFormData;
    }) => {
      const payload = {
        user: {
          category: UserCategory.TenantAccount,
          username: formData.user.username,
          password: formData.user.password1,
          name: formData.user.name,
          option: {
            ...formData.base,
            ...(formData.tenant as unknown as UserTenantOptionData),
            ...(formData.member as unknown as UserMemberOptionData),
            ...(formData.apiAccess as unknown as UserApiAccessFormData),
            ...(formData.webAccess as unknown as UserWebAccessFormData),
          },
        },
      };

      if (
        payload.user.option.member_deposit_charge_from ==
        UserOptionMemberChargeFrom.Member
      ) {
        payload.user.option.member_deposit_charge_time =
          UserOptionMemberChargeTime.Verified;
      }
      if (
        payload.user.option.member_withdraw_charge_from ==
        UserOptionMemberChargeFrom.Member
      ) {
        payload.user.option.member_withdraw_charge_time =
          UserOptionMemberChargeTime.Verified;
      }

      await UserService.create(payload).run();
    }
  );

  const handleShow = () => {
    setCurrentStep(0);

    setUserFormData({
      username: "",
      password1: "",
      password2: "",
      name: "",
      telegram_group_id: "",
    });

    setBaseOptionFormData({
      address: "",
      callback: "",
      agency_rate: 0,
    });

    setTenantOptionFormData({
      tenant_deposit_expired: "",
      tenant_deposit_rate_mode: 0,
      tenant_deposit_rate_data: "",
      tenant_deposit_charge_mode: 0,
      tenant_deposit_charge_rate: 0,
      tenant_deposit_charge_data: "",
      tenant_deposit_amount_min: "",
      tenant_deposit_amount_max: "",
      tenant_deposit_block_seconds: 0,
      tenant_deposit_block_times: 0,
      tenant_deposit_block_span: 0,
      tenant_withdraw_expired: "",
      tenant_withdraw_rate_mode: 0,
      tenant_withdraw_rate_data: "",
      tenant_withdraw_charge_mode: 0,
      tenant_withdraw_charge_rate: 0,
      tenant_withdraw_charge_data: "",
      tenant_withdraw_amount_min: "",
      tenant_withdraw_amount_max: "",
      tenant_withdraw_block_seconds: 0,
      tenant_withdraw_block_times: 0,
      tenant_withdraw_block_span: 0,
    });

    setMemberOptionFormData({
      member_deposit_callback: "",
      member_deposit_expired: "",
      member_deposit_currency: "USDT",
      member_deposit_round: TransferCalcRound.unchanged,
      member_deposit_rate_mode: 0,
      member_deposit_rate_data: "",
      member_deposit_charge_time: 0,
      member_deposit_charge_from: 0,
      member_deposit_charge_mode: 0,
      member_deposit_charge_rate: 0,
      member_deposit_charge_data: "",
      member_deposit_amount_min: "",
      member_deposit_amount_max: "",
      member_deposit_block_seconds: 0,
      member_deposit_block_times: 0,
      member_deposit_block_span: 0,
      member_withdraw_callback: "",
      member_withdraw_expired: "",
      member_withdraw_currency: "USDT",
      member_withdraw_round: TransferCalcRound.unchanged,
      member_withdraw_rate_mode: 0,
      member_withdraw_rate_data: "",
      member_withdraw_charge_time: 0,
      member_withdraw_charge_from: 0,
      member_withdraw_charge_mode: 0,
      member_withdraw_charge_rate: 0,
      member_withdraw_charge_data: "",
      member_withdraw_amount_min: "",
      member_withdraw_amount_max: "",
      member_withdraw_block_seconds: 0,
      member_withdraw_block_times: 0,
      member_withdraw_block_span: 0,
    });

    setApiAccessOptionFormData({
      api_access_mode: 0,
      api_white_ips: "",
      api_member_deposit_white_ips: "",
      api_member_withdraw_white_ips: "",
      api_black_ips: "",
      api_member_deposit_black_ips: "",
      api_member_withdraw_black_ips: "",
    });

    setWebAccessOptionFormData({
      web_access_mode: 0,
      web_white_ips: "",
      web_black_ips: "",
    });

    setIsOpen(true);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleSubmit = async (_formData: {
    user: UserUserFormData;
    base: UserBaseOptionFormData;
    tenant: UserTenantOptionFormData;
    member: UserMemberOptionFormData;
    apiAccess: UserApiAccessFormData;
    webAccess: UserWebAccessFormData;
  }) => {
    const formData = {
      ..._formData,
      apiAccess: StringHelper.replaceBreakLineValues(_formData.apiAccess, [
        "api_white_ips",
        "api_member_withdraw_white_ips",
        "api_member_deposit_white_ips",
        "api_black_ips",
        "api_member_withdraw_black_ips",
        "api_member_deposit_black_ips",
      ]),
      webAccess: StringHelper.replaceBreakLineValues(_formData.webAccess, [
        "web_white_ips",
        "web_black_ips",
      ]),
    };

    const res = await createUser(formData);
    if (res as unknown) {
      // error
      onFail();
      return;
    }

    setIsOpen(false);
    onCreate();
  };

  return (
    <>
      <Button type="primary" icon={<PlusCircleTwoTone />} onClick={handleShow}>
        新增商戶
      </Button>

      <Modal
        centered
        maskClosable={false}
        width={760}
        title="新增商戶"
        open={isOpen}
        footer={[]}
        onCancel={handleCancel}
      >
        <Steps
          size="small"
          className="mt:16 px:12"
          current={currentStep}
          items={[
            { title: "基本資料" },
            { title: "商戶設定" },
            { title: "會員設定" },
            { title: "串接 IP 管理" },
            { title: "登入 IP 管理" },
          ]}
        ></Steps>

        {userFormData && baseOptionFormData && (
          <Step0Form
            data={{ user: userFormData, base: baseOptionFormData }}
            visible={currentStep === 0}
            onNext={(data) => {
              setUserFormData((originData) => ({
                ...originData,
                ...data.user,
              }));
              setBaseOptionFormData((originData) => ({
                ...originData,
                ...data.base,
              }));
              setCurrentStep(1);
            }}
            onCancel={handleCancel}
          />
        )}

        {tenantOptionFormData && (
          <Step1Form
            data={tenantOptionFormData}
            visible={currentStep === 1}
            onPrev={() => setCurrentStep(0)}
            onNext={(data) => {
              setTenantOptionFormData((originData) => ({
                ...originData,
                ...data,
              }));
              setCurrentStep(2);
            }}
            onCancel={handleCancel}
          />
        )}

        {memberOptionFormData && (
          <Step2Form
            data={memberOptionFormData}
            visible={currentStep === 2}
            onPrev={() => setCurrentStep(1)}
            onNext={(data) => {
              setMemberOptionFormData((originData) => ({
                ...originData,
                ...data,
              }));
              setCurrentStep(3);
            }}
            onCancel={handleCancel}
          />
        )}

        {apiAccessOptionFormData && (
          <Step3Form
            data={apiAccessOptionFormData}
            visible={currentStep === 3}
            onPrev={() => setCurrentStep(2)}
            onNext={(data) => {
              setApiAccessOptionFormData((originData) => ({
                ...originData,
                ...data,
              }));
              setCurrentStep(4);
            }}
            onCancel={handleCancel}
          />
        )}

        {webAccessOptionFormData && (
          <Step4Form
            data={webAccessOptionFormData}
            visible={currentStep === 4}
            loading={state.loading}
            onPrev={() => setCurrentStep(3)}
            onNext={async (data) => {
              setWebAccessOptionFormData((originData) => ({
                ...originData,
                ...data,
              }));

              await handleSubmit({
                user: userFormData!,
                base: baseOptionFormData!,
                tenant: tenantOptionFormData!,
                member: memberOptionFormData!,
                apiAccess: apiAccessOptionFormData!,
                webAccess: {
                  ...webAccessOptionFormData,
                  ...data,
                },
              });
            }}
            onCancel={handleCancel}
          />
        )}
      </Modal>
    </>
  );
};
